import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
} from "react";
import Bytes from "../../shared/Bytes";
import LoadingText from "../../shared/LoadingText";
import ErrorMessage from "../../shared/error/ErrorMessage";
import DismissIcon from "../../shared/icons/DismissIcon";
import { useTracking, EVENTS } from "../../Tracking";
import { captureException } from "@sentry/core";

export const AttachmentUrlCacheContext = React.createContext({});

export const AttachmentUrlCacheProvider = ({ children }) => {
  const [cache, setCache] = useState({});
  const [queue, setQueue] = useState([]);

  const loadItem = useCallback(
    async function load({ id, client }) {
      if (cache[id] && new Date(cache[id].urlExpires) > new Date()) return;
      try {
        const result = await client.get(id);
        setCache((c) => ({
          ...c,
          [id]: result,
        }));
      } catch (error) {
        captureException(error);
      } finally {
      }
    },
    [cache]
  );

  const process = useCallback(
    async (queue) => {
      if (!queue.length) return;
      const next = queue[0];
      setQueue((q) => q.filter(({ id }) => id !== next.id));
      await loadItem(next);
    },
    [loadItem]
  );

  useEffect(() => {
    process(queue);
  }, [process, queue]);

  const load = useCallback(
    ({ id, client }) => setQueue((q) => [...q, { id, client }]),
    []
  );

  return (
    <AttachmentUrlCacheContext.Provider
      value={{
        urls: Object.keys(cache).reduce((x, key) => {
          x[key] = cache[key].url;

          return x;
        }, {}),
        load,
      }}
    >
      {children}
    </AttachmentUrlCacheContext.Provider>
  );
};

function useFileUrl({ file, client }) {
  const { urls, load } = useContext(AttachmentUrlCacheContext);

  useEffect(() => {
    if (file) load({ id: file.id, client });
  }, [file, load, client]);

  return file ? urls[file.id] : null;
}

export function useFileUrls(files) {
  const [isLoading, setIsLoading] = useState(true);
  const { urls, load } = useContext(AttachmentUrlCacheContext);

  const loadFiles = useCallback(
    async function loadFiles(files) {
      setIsLoading(true);
      try {
        for (const { file, client } of files) {
          await load({ id: file.id, client });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [load]
  );

  useEffect(() => {
    loadFiles(files);
  }, [files, loadFiles]);

  return {
    isLoading,
    result: Object.keys(urls).reduce((acc, x) => {
      acc[x] = urls[x];

      return acc;
    }, {}),
  };
}

function ValidationError({
  error: { contentType, maxNumFiles, maxSize, maxTotalSize, nameConflict },
}) {
  if (contentType) return <span>Ugyldig filtype. Velg en annen fil.</span>;

  if (maxNumFiles)
    return <span>Du kan kun laste opp {maxNumFiles} filer.</span>;

  if (maxSize)
    return (
      <span>
        Maks filstørrelse er <Bytes bytes={maxSize} />.
      </span>
    );
  if (maxTotalSize)
    return (
      <span>
        Maks total filstørrelse er <Bytes bytes={maxTotalSize} />.
      </span>
    );

  if (nameConflict)
    return <span>Du har allerede lastet opp et vedlegg med dette navnet.</span>;

  return null;
}

const FileItem = ({
  queued,
  client,
  attachment,
  state,
  scaleResult,
  renameResult,
  validationError,
  onClickRemove,
}) => {
  const tracking = useTracking();
  const fileUrl = useFileUrl({ file: attachment, client });

  const name = queued ? renameResult || queued.name : attachment.name;
  const size = queued ? queued.size : attachment.contentLength;

  const [queuedUri, setQueuedUri] = useState(null);

  const uri = queuedUri || fileUrl;

  useEffect(() => {
    const queuedUri = queued
      ? scaleResult
        ? URL.createObjectURL(scaleResult.blob)
        : URL.createObjectURL(queued)
      : null;

    setQueuedUri(queuedUri);

    return () => URL.revokeObjectURL(queuedUri);
  }, [queued, scaleResult]);

  const anchorEl = useRef();

  return (
    <div
      className="AttachmentsForm-box-preview-item"
      onClick={(event) => {
        if (event.target !== anchorEl.current) event.preventDefault();
      }}
    >
      <div className="AttachmentsForm-box-preview-item-body">
        <span>
          {uri ? (
            <a
              href={uri}
              target="_blank"
              rel="noreferrer noopener"
              ref={anchorEl}
            >
              {name}
            </a>
          ) : (
            name
          )}
        </span>
        <span>
          {" "}
          <Bytes bytes={size} />
        </span>
        {state ? (
          <span>
            {" "}
            <LoadingText text={state} />
          </span>
        ) : null}
        {scaleResult ? (
          <span>
            <br />
            <span>
              Forminsket til <Bytes bytes={scaleResult.size} />. <br />
              <em>
                ({scaleResult.originalDimensions.width}x
                {scaleResult.originalDimensions.height} px til{" "}
                {scaleResult.dimensions.width}x{scaleResult.dimensions.height}{" "}
                px)
              </em>
            </span>
          </span>
        ) : null}
        {queued && validationError ? (
          <span>
            <br />
            <br />
            <ErrorMessage component="span">
              <ValidationError error={validationError} />
            </ErrorMessage>
          </span>
        ) : null}
      </div>
      {!state && onClickRemove ? (
        <button
          className="AttachmentsForm-box-preview-item-remove"
          disabled={state}
          onClick={(event) => {
            event.stopPropagation();
            event.preventDefault();
            tracking.event({
              ...EVENTS.ATTACHMENTS.REMOVE,
              label: name,
            });
            onClickRemove();
          }}
        >
          <div className="AttachmentsForm-box-preview-item-remove-icon">
            <DismissIcon size={16} />
          </div>
        </button>
      ) : null}
    </div>
  );
};

export default FileItem;
